/**
 * Minimal i18n shim — pure function lookup with no external dependency.
 * Keeps the bundle slim; expand to react-i18next if we need pluralization or
 * ICU messages later.
 *
 * Usage:
 *   import { t, setLocale, formatDate } from '@/lib/i18n';
 *   t('dashboard.welcome', { name: 'Marga' })
 *   formatDate(new Date(), { dateStyle: 'medium' })
 */

type Dict = Record<string, string>;

const en: Dict = {
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.copy': 'Copy',
  'common.search': 'Search',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.publish': 'Publish',
  'common.draft': 'Draft',
  'common.published': 'Published',
  'common.unsaved': 'Unsaved',
  'common.saved': 'Saved',
  'common.saving': 'Saving…',
  'common.tracking_domains': 'Tracking domains',

  'auth.signup.title': 'Create your workspace',
  'auth.login.title': 'Sign in',
  'auth.magic_link.sent': 'Check your inbox',
  'auth.magic_link.hint': "We'll send you a one-click sign-in link.",

  'nav.dashboard': 'Delivery Health',
  'nav.templates': 'Templates',
  'nav.gallery': 'Gallery',
  'nav.sends': 'Sends',
  'nav.experiments': 'Experiments',
  'nav.tokens': 'Design tokens',
  'nav.smtp': 'SMTP providers',
  'nav.senders': 'Senders',
  'nav.automations': 'Automations',
  'nav.integrations': 'Integrations',
  'nav.api_keys': 'API keys',
  'nav.webhooks': 'Webhooks',
  'nav.settings': 'Settings',
  'nav.tracking_domains': 'Tracking domains',
  'nav.feature_flags': 'Feature flags',

  'crumbs.configure': 'Configure',
  'crumbs.developers': 'Developers',
  'crumbs.experiments': 'Experiments',
  'crumbs.flow': 'Flow',
  'crumbs.settings': 'Settings',
  'crumbs.editor': 'Editor',

  'dashboard.welcome': 'Morning, {name}',
  'templates.empty': 'No templates yet',
  'templates.new': 'New template',
  'editor.save.saving': 'Saving…',
  'editor.save.saved': 'Saved',
  'editor.save.dirty': 'Unsaved',
  'editor.publish': 'Publish',

  'errors.network': 'Network error — check your connection and retry.',
  'errors.unauthorized': 'Your session expired. Please sign in again.',
  'errors.unknown': 'Something went wrong. Try again or contact support.',
};

const es: Dict = {
  'common.save': 'Guardar',
  'common.cancel': 'Cancelar',
  'common.delete': 'Eliminar',
  'common.loading': 'Cargando…',
  'common.retry': 'Reintentar',
  'common.copy': 'Copiar',
  'common.search': 'Buscar',
  'common.close': 'Cerrar',
  'common.back': 'Atrás',
  'common.continue': 'Continuar',
  'common.publish': 'Publicar',
  'common.draft': 'Borrador',
  'common.published': 'Publicado',
  'common.unsaved': 'Sin guardar',
  'common.saved': 'Guardado',
  'common.saving': 'Guardando…',

  'auth.signup.title': 'Crea tu workspace',
  'auth.login.title': 'Iniciar sesión',
  'auth.magic_link.sent': 'Revisa tu bandeja',
  'auth.magic_link.hint': 'Te enviamos un enlace de acceso con un click.',

  'nav.dashboard': 'Salud de entrega',
  'nav.templates': 'Plantillas',
  'nav.gallery': 'Galería',
  'nav.sends': 'Envíos',
  'nav.experiments': 'Experimentos',
  'nav.tokens': 'Design tokens',
  'nav.smtp': 'Proveedores SMTP',
  'nav.senders': 'Remitentes',
  'nav.automations': 'Automatizaciones',
  'nav.integrations': 'Integraciones',
  'nav.api_keys': 'API keys',
  'nav.webhooks': 'Webhooks',
  'nav.settings': 'Ajustes',
  'nav.tracking_domains': 'Dominios de tracking',
  'nav.feature_flags': 'Feature flags',

  'crumbs.configure': 'Configurar',
  'crumbs.developers': 'Desarrolladores',
  'crumbs.experiments': 'Experimentos',
  'crumbs.flow': 'Flujo',
  'crumbs.settings': 'Ajustes',
  'crumbs.editor': 'Editor',

  'dashboard.welcome': 'Buen día, {name}',
  'templates.empty': 'Aún no hay plantillas',
  'templates.new': 'Nueva plantilla',
  'editor.save.saving': 'Guardando…',
  'editor.save.saved': 'Guardado',
  'editor.save.dirty': 'Sin guardar',
  'editor.publish': 'Publicar',

  'errors.network': 'Error de red — verifica tu conexión y reintenta.',
  'errors.unauthorized': 'Tu sesión expiró. Vuelve a iniciar sesión.',
  'errors.unknown': 'Algo salió mal. Reintenta o contacta soporte.',
};

const DICTIONARIES: Record<string, Dict> = { en, es };

let currentLocale = detectInitialLocale();

function detectInitialLocale(): string {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem('emailforge.locale');
  if (stored && DICTIONARIES[stored]) return stored;
  const navLang = window.navigator.language?.split('-')[0] ?? 'en';
  return DICTIONARIES[navLang] ? navLang : 'en';
}

export function getLocale(): string {
  return currentLocale;
}

export function setLocale(locale: string): void {
  if (!DICTIONARIES[locale]) throw new Error(`Unknown locale: ${locale}`);
  currentLocale = locale;
  // Clear the Intl formatter cache so date/number outputs swap immediately.
  // The cache lives in the closure below; declared before this fn so we
  // reach it via the module-level `formatterCache` reference.
  formatterCache.clear();
  if (typeof window !== 'undefined') window.localStorage.setItem('emailforge.locale', locale);
  window.dispatchEvent(new Event('emailforge:locale-changed'));
}

export function t(key: string, params?: Record<string, string | number>): string {
  const dict = DICTIONARIES[currentLocale] ?? DICTIONARIES.en!;
  const base = dict[key] ?? DICTIONARIES.en![key] ?? key;
  if (!params) return base;
  return Object.entries(params).reduce((s, [k, v]) => s.replace(`{${k}}`, String(v)), base);
}

export const SUPPORTED_LOCALES = Object.keys(DICTIONARIES) as Array<keyof typeof DICTIONARIES>;

// ---------------------------------------------------------------------------
// Locale-aware formatters
//
// These thin-wrap `Intl.*` so that:
//   1. callers don't have to import `Intl.NumberFormat` directly,
//   2. the locale stays in sync with whatever `setLocale()` last picked, and
//   3. we have a single seam to swap if we ever migrate to a real i18n lib.
//
// We instantiate the formatters lazily and cache by locale+options-key — `new
// Intl.NumberFormat(...)` is non-trivial work and dashboards format hundreds
// of numbers per render.

const formatterCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();

function cachedNumberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n:${locale}:${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, cached);
  }
  return cached as Intl.NumberFormat;
}

function cachedDateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d:${locale}:${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, cached);
  }
  return cached as Intl.DateTimeFormat;
}

function cachedRelativeTimeFormat(locale: string): Intl.RelativeTimeFormat {
  const key = `r:${locale}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    formatterCache.set(key, cached);
  }
  return cached as Intl.RelativeTimeFormat;
}

/** Locale-aware number formatting. */
export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
  return cachedNumberFormat(currentLocale, options).format(value);
}

/** Locale-aware percentage. Pass the raw fraction (e.g. `0.42` → "42%"). */
export function formatPercent(value: number, fractionDigits = 1): string {
  return formatNumber(value, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** Locale-aware date. Defaults to medium-style date + short-style time. */
export function formatDate(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = value instanceof Date ? value : new Date(value);
  return cachedDateTimeFormat(currentLocale, options).format(d);
}

/**
 * Locale-aware relative time. Picks the largest unit that still has whole
 * numbers — "5 minutes ago" rather than "300 seconds ago" — then defers
 * "just now" / "yesterday" wording to the browser.
 */
export function formatRelativeTime(value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  const diffMs = d.getTime() - Date.now();
  const fmt = cachedRelativeTimeFormat(currentLocale);
  const abs = Math.abs(diffMs);
  if (abs < 60_000) return fmt.format(Math.round(diffMs / 1000), 'second');
  if (abs < 3_600_000) return fmt.format(Math.round(diffMs / 60_000), 'minute');
  if (abs < 86_400_000) return fmt.format(Math.round(diffMs / 3_600_000), 'hour');
  if (abs < 604_800_000) return fmt.format(Math.round(diffMs / 86_400_000), 'day');
  if (abs < 2_592_000_000) return fmt.format(Math.round(diffMs / 604_800_000), 'week');
  if (abs < 31_536_000_000) return fmt.format(Math.round(diffMs / 2_592_000_000), 'month');
  return fmt.format(Math.round(diffMs / 31_536_000_000), 'year');
}
